<template>
  <div>
    <v-container v-if="!this.componentReady" class="text-center">
      <v-progress-circular :size="50" indeterminate color="primary"></v-progress-circular>
    </v-container>
    <v-container v-else>
      <v-row>
        <v-col>
          <v-progress-linear :value="overallProgress" color="grey lighten-2" height="25" rounded>
            <strong>{{ $t("components.timeline.overallProgress") }} {{ Math.ceil(overallProgress) }}%</strong>
          </v-progress-linear>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(1)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(0)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(0)">mdi-file-document</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.orderReceival.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>

        <v-col>
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(2)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(1)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(1)">mdi-ruler-square</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.measuring.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>

        <v-col>
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(3)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(2)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(2)">mdi-drawing-box</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.designing.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>

        <v-col>
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(4)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(3)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(3)">mdi-factory</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.production.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>

        <v-col>
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(5)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(4)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(4)">mdi-truck-delivery</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.delivery.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>

        <v-col v-if="!hideInstallation">
          <v-card class="pa-1" :min-width="getWidth()+'%'" elevation="0" @click="selectPhase(6)">
            <v-hover v-slot="{ hover }">
              <v-card class="d-flex flex-column justify-center" elevation="0">
                <v-container class="d-flex flex-row justify-center">
                  <v-avatar :color="hover ? '#ffffff' : getColor(5)" size="64" data-voverviewtourstep="4">
                    <v-icon large :color="!hover ? '#ffffff' : getColor(5)">mdi-tools</v-icon>
                  </v-avatar>
                </v-container>
                <v-container class="d-flex flex-row justify-center">{{ $t('components.timeline.phases.assembly.name') }}
                </v-container>
              </v-card>
            </v-hover>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-divider></v-divider>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card v-if="isSelected(1)" elevation="0">
            <v-card-title>{{ $t("components.timeline.phases.orderReceival.name") }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div>
                    <p>{{ $t("components.timeline.phases.orderReceival.text0a") }}</p>
                    <p>{{ $t("components.timeline.phases.orderReceival.text0b") }}</p>
                    <p>{{ $t("components.timeline.phases.orderReceival.text0c") }}</p>
                    <p>
                      <b>{{ workOrder.MasterOrder.Clerk.DisplayName }}</b><br />
                      {{ workOrder.MasterOrder.Clerk.Email }}<br />
                      {{ workOrder.MasterOrder.Clerk.PhoneNumber }}<br />
                    </p>
                  </div>
                  <v-list>
                    <v-list-item>
                      <v-list-item-action><v-icon x-large>mdi-file-document-check</v-icon></v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t("components.timeline.phases.orderReceival.orderConfirmation") }}</v-list-item-title>
                          <v-list-item-subtitle
                            v-if="getMasterOrderOrderConfirmation(workOrder).length < 1">
                            {{ $t("components.timeline.phases.orderReceival.noDocument") }}</v-list-item-subtitle>
                          <v-list-item-subtitle v-else
                            @click="downloadDocument(workOrder.MasterOrderId, getMasterOrderOrderConfirmation(workOrder))">
                            <a>{{ $t("components.timeline.phases.orderReceival.download") }}</a>
                          </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-mitarbeiter-6.jpeg')" min-height="300"
                    max-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-else-if="isSelected(2)" elevation="0">
            <v-card-title>{{ $t("components.timeline.phases.measuring.name") }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div v-if="getColor(1) == 'grey'">
                    <p>{{ $t("components.timeline.phases.measuring.text0a") }}</p>
                    <p>{{ $t("components.timeline.phases.measuring.text0b") }}</p>
                  </div>

                  <div v-if="getColor(1) == 'orange'">
                    <p>
                      {{ $t("components.timeline.phases.measuring.text0a") }}
                    </p>
                    <v-card elevation="0" v-if="measurementOrder.Id != undefined">
                      <v-card-title>{{ $t("components.timeline.phases.measuring.appointment") }}</v-card-title>
                      <v-card-subtitle v-if="measurementOrder.MeasuringDate != null">{{
                      $d(Date.parse(this.measurementOrder.MeasuringDate), 'short') }}</v-card-subtitle>
                      <v-card-subtitle v-else>{{ $t("components.timeline.phases.measuring.noAppointmentYet") }}</v-card-subtitle>
                    </v-card>
                    <div v-if="measurementOrder.Id != undefined">
                      <p>
                        <b>{{ measurementOrder.Technician.DisplayName }}</b><br />
                        {{ measurementOrder.Technician.Email }}<br />
                        {{ measurementOrder.Technician.PhoneNumber }}<br />
                      </p>
                    </div>
                  </div>

                  <div v-if="getColor(1) == 'green'">
                    <p>{{ $t("components.timeline.phases.measuring.text2") }}</p>
                  </div>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-mitarbeiter-27.jpg')" min-height="300"
                    max-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-else-if="isSelected(3)" elevation="0">
            <v-card-title>{{ $t("components.timeline.phases.designing.name") }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div v-if="getColor(2) == 'grey'">
                    <p>{{ $t("components.timeline.phases.designing.text0") }}</p>
                  </div>
                  <div v-if="getColor(2) == 'orange'">
                    <p>{{ $t("components.timeline.phases.designing.text1") }}</p>
                  </div>
                  <div v-if="getColor(2) == 'green'">
                    <p>{{ $t("components.timeline.phases.designing.text2") }}</p>
                  </div>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-mitarbeiter.jpg')" max-height="300"
                    min-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-else-if="isSelected(4)" elevation="0">
            <v-card-title>{{ $t("components.timeline.phases.production.name") }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div v-if="getColor(3) == 'grey'">
                    <p>{{ $t("components.timeline.phases.production.text0") }}</p>
                  </div>
                  <div v-if="getColor(3) == 'orange'">
                    <p>{{ $t("components.timeline.phases.production.text1") }}</p>
                  </div>
                  <div v-if="getColor(3) == 'green'">
                    <p>{{ $t("components.timeline.phases.production.text2") }}</p>
                  </div>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-mitarbeiter-15.jpg')" max-height="300"
                    min-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-else-if="isSelected(5)" elevation="0">
            <v-card-title>{{ $t("components.timeline.phases.delivery.name") }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div v-if="getColor(4) == 'grey'">
                    <p>{{ $t("components.timeline.phases.delivery.text0") }}</p>
                  </div>
                  <div v-if="getColor(4) == 'orange'">
                    <p>{{ $t("components.timeline.phases.delivery.text1") }}</p>
                    <v-card elevation="0">
                      <v-card-title>{{ $t("components.timeline.phases.delivery.assumedDeliveryDate") }}</v-card-title>
                      <v-card-subtitle v-if="this.timeLineConfig.expectedDeliveryDate == null">
                        <i>{{ $t("components.timeline.phases.delivery.noAppointmentYet") }}</i>
                      </v-card-subtitle>
                      <v-card-subtitle v-else-if="this.timeLineConfig.showExpectedDeliveryDateAsCalendarWeek == true">
                        {{ $t("components.timeline.phases.delivery.calendarWeek") }} {{ convertToLuxonDate(new Date(Date.parse(this.timeLineConfig.expectedDeliveryDate))).weekNumber }}
                      </v-card-subtitle>
                      <v-card-subtitle v-else>
                        {{ $d(Date.parse(this.timeLineConfig.expectedDeliveryDate), 'short') }}
                      </v-card-subtitle>
                    </v-card>
                    <v-alert v-if="this.timeLineConfig.isExpectedDeliveryToWarehouse == true" type="info" outlined text>
                      <h3>{{ $t('components.timeline.phases.delivery.warehouseDeliveryInfo.title') }}</h3>
                      {{ $t('components.timeline.phases.delivery.warehouseDeliveryInfo.subtitle') }}
                    </v-alert>
                  </div>
                  <div v-if="getColor(4) == 'green'">
                    <p>{{ $t("components.timeline.phases.delivery.text2") }}</p>
                    <v-card elevation="0">
                      <v-card-title>{{ $t("components.timeline.phases.delivery.deliveryDate") }}</v-card-title>
                      <v-card-subtitle v-if="this.timeLineConfig.actualDeliveryDate == null">{{ $t("components.timeline.phases.delivery.noDeliveryDate") }}</v-card-subtitle>
                      <v-card-subtitle v-else>{{ $d(Date.parse(this.timeLineConfig.actualDeliveryDate), 'short') }}
                      </v-card-subtitle>
                    </v-card>
                    <v-alert v-if="this.timeLineConfig.wasDeliveredToWarehouse == true" type="info" outlined text>
                      <h3>{{ $t('components.timeline.phases.delivery.warehouseDeliveryInfo.title') }}</h3>
                      {{ $t('components.timeline.phases.delivery.warehouseDeliveryInfo.subtitle') }}
                    </v-alert>
                  </div>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-transporter.jpeg')" max-height="300"
                    min-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-else-if="isSelected(6)" elevation="0">
            <v-card-title>{{ $t('components.timeline.phases.assembly.name') }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col lg="6" cols="12">
                  <div v-if="getColor(5) == 'grey'">
                    <p>{{ $t('components.timeline.phases.assembly.text0') }}</p>
                  </div>
                  <div v-if="getColor(5) == 'orange'">
                    <p>{{ $t('components.timeline.phases.assembly.text1') }}</p>
                    <p>
                      <b>{{ $t('components.timeline.phases.assembly.dateMayVary') }}</b>
                    </p>
                    <v-card elevation="0">
                      <v-card-title>{{ $t('components.timeline.phases.assembly.assumedAssemblyDate') }}</v-card-title>
                      <v-card-subtitle v-if="this.timeLineConfig.expectedAssemblyDate == null">
                        <i>{{ $t('components.timeline.phases.assembly.noAppointmentYet') }}</i>
                      </v-card-subtitle>
                      <v-card-subtitle v-else-if="this.timeLineConfig.showExpectedAssemblyDateAsCalendarWeek == true">
                        {{ $t('components.timeline.phases.assembly.calendarWeek') }} {{ convertToLuxonDate(new Date(Date.parse(this.timeLineConfig.expectedAssemblyDate))).weekNumber }}
                      </v-card-subtitle>
                      <v-card-subtitle v-else>
                        {{ $d(Date.parse(this.timeLineConfig.expectedAssemblyDate), 'short') }}
                      </v-card-subtitle>
                    </v-card>
                  </div>
                  <div v-if="getColor(5) == 'green'">
                    <p>{{ $t('components.timeline.phases.assembly.text2') }}</p>
                    <v-card elevation="0">
                      <v-card-title>{{ $t('components.timeline.phases.assembly.assemblyDate') }}</v-card-title>
                      <v-card-subtitle v-if="this.timeLineConfig.actualAssemblyDate == null">{{ $t('components.timeline.phases.assembly.noAssemblyDate') }}</v-card-subtitle>
                      <v-card-subtitle v-else>{{ $d(Date.parse(this.timeLineConfig.actualAssemblyDate), 'short') }}</v-card-subtitle>
                    </v-card>
                    <v-list>
                      <v-list-item>
                        <v-list-item-action>
                          <v-icon x-large>mdi-clipboard-text</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>{{ $t('components.timeline.phases.assembly.assemblyReport') }}</v-list-item-title>
                          <v-list-item-subtitle
                            v-if="getFieldOrderAssemblyReport(workOrder, workOrder.WorkOrderFieldOrders[0].FieldOrder).length < 1">
                            kein Dokument verfügbar</v-list-item-subtitle>
                          <v-list-item-subtitle v-else
                            @click="downloadDocument(workOrder.MasterOrderId, getFieldOrderAssemblyReport(workOrder, workOrder.WorkOrderFieldOrders[0].FieldOrder))">
                            <a>{{ $t('components.timeline.phases.assembly.download') }}</a>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </div>
                </v-col>
                <v-col lg="6" cols="12" class="d-none d-lg-flex">
                  <v-img :src="require('@/assets/leeb-images/leeb-balkone-mitarbeiter-4.jpeg')" max-height="300"
                    min-height="300"></v-img>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import apiClient from '../plugins/CustomerPortalApiClient/client';
import { DateTime } from 'luxon';
import {saveAs} from 'file-saver';

export default {
  name: "time-line",
  data() {
    return {
      timeLineConfig: {},
      workOrder: {},
      measurementOrder: {},
      colors: [],
      overallProgress: 0,
      hideInstallation: false,
      selectedPhase: 0,
      previousWorksHeaders: [
        {text: 'Vorarbeit', value: 'PreviousWork.Description', sortable: false, align: 'start'},
        {text: 'Erledigt?', value: 'IsDone', sortable: false}
      ],
      selectedPreviousWork: {},
      documents: [],
      assemblyReport: undefined,
      // ready to show?
      componentReady: false
    }
  },
  props: {
    workOrderId: Number,
  },
  methods: {
    selectPhase(val) {
      this.selectedPhase = val;
      this.$forceUpdate();
    },
    getColor(position) {
      return this.colors[position];
    },
    getWidth() {
      return 100/(this.colors.length-(this.hideInstallation?1:0));
    },
    getFieldOrderAssemblyReport(workOrder, fieldOrder) {
      return apiClient.getFieldOrderAssemblyReport(workOrder.MasterOrder.OrderNumber, fieldOrder, this.documents);
    },
    getMasterOrderOrderConfirmation(workOrder) {
      return apiClient.getOrderConfirmations(workOrder.MasterOrder.OrderNumber, this.documents);
    },
    isDividerVertical() {
      return ['lg', 'xl'].findIndex((val) => val == this.$vuetify.breakpoint.name) != -1;
    },
    /**
     * Checks if current phase is selected.
     */
    isSelected(pos) {
      return this.selectedPhase == pos;
    },
    /**
     * Converts given Date to a Luxon DateTime.
     * @param date An ordinary JavaScript date.
     */
    convertToLuxonDate(date) {
      return DateTime.fromJSDate(date);
    },
    loadWorkOrder(workOrderId) {
      apiClient.loadWorkOrder(
        this.$store.getters.getToken(),
        this.$store.getters.customerNumber(),
        workOrderId
      ).then((workOrder) => {
        this.workOrder = workOrder;
      }).catch((error) => {
        console.error(error);
        this.$snackbar(this.$t("components.timeline.errors.loadWorkOrder"), 'red darken-1');
      });
    },
    loadMeasurementOrder(measurementOrderId) {
      apiClient.loadMeasurementOrder(
        this.$store.getters.getToken(),
        this.$store.getters.customerNumber(),
        measurementOrderId
      ).then((measurementOrder) => {
        this.measurementOrder = measurementOrder;
      }).catch((error) => {
        console.error(error);
        this.$snackbar(this.$t("components.timeline.errors.loadMeasurementOrder"), 'red darken-1');
      })
    },
    loadDocuments(masterOrderId) {
      apiClient.loadDocuments(
        this.$store.getters.getToken(),
        this.$store.getters.customerNumber(),
        masterOrderId
      ).then((documents) => {
        this.documents = documents;
      }).catch((error) => {
        console.error(error);
        this.$snackbar(this.$t("components.timeline.errors.loadDocuments"), 'red darken-1');
      });
    },
    downloadDocument(masterOrderId, document) {
      apiClient.loadDocumentsDownload(
        this.$store.getters.getToken(),
        this.$store.getters.customerNumber(),
        masterOrderId,
        document.FolderId,
        document.DocumentId
      ).then((documentToDownload) => {
        saveAs(
          documentToDownload.Base64Content,
          documentToDownload.FileName,
          'base64'
        );
      }).catch((error) => {
        console.error(error);
        this.$snackbar(this.$t("components.timeline.errors.downloadDocument"), 'red darken-1');
      });
    },
    /**
     * Initialize pre-selected phase
     */
    handleInit() {
      this.timeLineConfig = apiClient.calculateTimeLineConfig(this.workOrder.WorkOrderFieldOrders, this.workOrder.Events, this.workOrder.WorkOrderFlag, this.workOrder.IsInstallationOrder);
      this.colors = this.timeLineConfig.colors;
      this.hideInstallation = this.timeLineConfig.hideInstallationOrder;
      this.overallProgress = this.timeLineConfig.percent;

      // First orange ...
      let lastTimelinePhase = this.colors.findIndex((value) => value === "orange");

      // ... or last green.
      if(lastTimelinePhase === -1) {
        lastTimelinePhase = this.colors.lastIndexOf("green");
      }

      if(lastTimelinePhase === -1) {
        this.selectedPhase = 0;
      } else {
        this.selectedPhase = lastTimelinePhase + 1;
      }
    }
  },
  created() {
    this.loadWorkOrder(this.workOrderId);
  },
  watch: {
    workOrderId() {
      this.componentReady = false;
      this.loadWorkOrder(this.workOrderId);
    },
    workOrder() {
      this.handleInit();
      this.loadDocuments(this.workOrder.MasterOrderId);
      if (this.workOrder.WorkOrderMeasurementOrders.length > 0) {
        this.loadMeasurementOrder(this.workOrder.WorkOrderMeasurementOrders[0].MeasurementOrder.Id);
      } else {
        this.measurementOrder = {};
      }
    },
    documents() {
      this.componentReady = true;
    }
  }
};
</script>